import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, FileSignature, ReceiptText, Image as ImageIcon, ClipboardList, File, Lock, Eye, Loader2, HardDrive } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card, Empty } from "@/components/common";

const CATS = [["vertrag", "Vertrag"], ["briefing", "Briefing"], ["rechnung", "Rechnung"], ["bericht", "Bericht"], ["bild", "Bild"], ["sonstiges", "Sonstiges"]];
const CAT_ICON = { vertrag: FileSignature, briefing: ClipboardList, rechnung: ReceiptText, bericht: FileText, bild: ImageIcon, sonstiges: File };
const MAX = 25 * 1024 * 1024;

const fmtSize = (b) => (!b ? "—" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const LOC = { de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-GB" };
const fmtDate = (iso, lang) => { try { return new Date(iso).toLocaleDateString(LOC[lang] || "de-CH", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };
export default function DocumentsManager({ clientId, readOnly = false }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canWrite = !readOnly && (user.role === "super_admin" || (user.role === "team_member" && user.team_role !== "viewer"));
  const [docs, setDocs] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("sonstiges");
  const [note, setNote] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);
  const [storage, setStorage] = useState(null);
  const [quotaInput, setQuotaInput] = useState("");
  const [savingQuota, setSavingQuota] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/clients/${clientId}/documents?lang=${lang}`).then((r) => setDocs(r.data));
  const loadStorage = () => api.get(`/admin/clients/${clientId}/storage`).then((r) => { setStorage(r.data); setQuotaInput(String(r.data.quota_gb)); }).catch(() => {});
  useEffect(() => { load(); loadStorage(); /* eslint-disable-next-line */ }, [clientId, lang]);

  const saveQuota = async () => {
    const q = parseFloat(quotaInput);
    if (isNaN(q) || q < 0) { toast.error(t("Bitte gültige GB-Zahl eingeben")); return; }
    setSavingQuota(true);
    try {
      const r = await api.post(`/admin/clients/${clientId}/storage-quota`, { quota_gb: q });
      setStorage(r.data); setQuotaInput(String(r.data.quota_gb));
      toast.success(t("Speicher-Kontingent gespeichert"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Speichern fehlgeschlagen")); } finally { setSavingQuota(false); }
  };

  const onUpload = async () => {
    if (!file) { toast.error(t("Bitte zuerst eine Datei wählen")); return; }
    if (file.size > MAX) { toast.error(t("Datei zu gross (max. 25 MB)")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      fd.append("category", category);
      fd.append("note", note);
      fd.append("visibility", visibility);
      await api.post(`/clients/${clientId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Dokument hochgeladen"));
      setTitle(""); setNote(""); setCategory("sonstiges"); setVisibility("internal"); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Upload fehlgeschlagen")); } finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name || "datei";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("Download fehlgeschlagen")); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success(t("Dokument gelöscht"));
      setDelId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Löschen fehlgeschlagen")); }
  };

  const toggleVisibility = async (d) => {
    const next = (d.visibility === "internal") ? "client" : "internal";
    try {
      await api.patch(`/documents/${d.id}/visibility`, { visibility: next });
      toast.success(next === "client" ? t("Für Kunde sichtbar geschaltet") : t("Auf intern gesetzt"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
  };

  return (
    <div className="space-y-5" data-testid="admin-documents">
      {storage && (
        <Card className="p-5" data-testid="admin-storage-panel">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">{t("Speicher-Kontingent")}</h3>
            <span className="text-xs text-gray-400" data-testid="admin-storage-usage">
              {fmtSize(storage.used_bytes)} {t("von")} {storage.quota_gb} GB · {storage.used_pct}%
            </span>
          </div>
          <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden mb-3">
            <div className="h-full rounded-full transition-all"
              style={{ width: `${Math.min(storage.used_pct || 0, 100)}%`, background: storage.used_pct >= 100 ? "#ef4444" : storage.used_pct >= 80 ? "#f59e0b" : "#A3E635" }} />
          </div>
          {canWrite && (
            <div className="flex items-end gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">{t("Kontingent (GB)")}</label>
                <input type="number" min="0" step="0.5" value={quotaInput} onChange={(e) => setQuotaInput(e.target.value)} data-testid="admin-storage-quota-input"
                  className="w-32 bg-[#0A0A0C] border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:border-[#A3E635] outline-none" />
              </div>
              <button onClick={saveQuota} disabled={savingQuota} data-testid="admin-storage-quota-save"
                className="bg-[#A3E635] text-black font-bold rounded-xl px-5 py-2.5 flex items-center gap-2 hover:brightness-105 disabled:opacity-50">
                {savingQuota ? <Loader2 className="w-4 h-4 animate-spin" /> : <HardDrive className="w-4 h-4" />} {t("Speichern")}
              </button>
            </div>
          )}
        </Card>
      )}
      {canWrite && (
        <Card className="p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">{t("Dokument hochladen")}</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1">{t("Titel")}</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title-input"
                className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:border-[#A3E635] outline-none" placeholder={t("Titel")} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1">{t("Kategorie")}</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category-select"
                className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:border-[#A3E635] outline-none">
                {CATS.map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-semibold text-gray-400 mb-1">{t("Notiz (optional)")}</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="doc-note-input"
              className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:border-[#A3E635] outline-none" placeholder={t("Notiz (optional)")} />
          </div>
          <div className="mt-3">
            <label className="block text-xs font-semibold text-gray-400 mb-1">{t("Sichtbarkeit")}</label>
            <select value={visibility} onChange={(e) => setVisibility(e.target.value)} data-testid="doc-visibility-select"
              className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:border-[#A3E635] outline-none">
              <option value="internal">{t("Intern (nur KEWAG)")}</option>
              <option value="client">{t("Kunde sichtbar")}</option>
            </select>
            <p className="text-[11px] text-amber-300/80 mt-1.5 flex items-center gap-1.5"><Lock className="w-3 h-3" /> {t("Neue Dokumente sind standardmässig intern. Der Kunde sieht sie erst nach bewusster Freigabe.")}</p>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-center">
            <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input"
              className="text-sm text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-4 file:py-2 file:text-white file:font-semibold file:cursor-pointer" />
            <button onClick={onUpload} disabled={busy} data-testid="doc-upload-btn"
              className="bg-[#A3E635] text-black font-bold rounded-xl px-5 py-2.5 flex items-center justify-center gap-2 hover:brightness-105 disabled:opacity-50">
              <Upload className="w-4 h-4" /> {busy ? t("Wird hochgeladen …") : t("Hochladen")}
            </button>
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        {docs === null ? null : docs.length === 0 ? (
          <div className="p-6"><Empty text={t("Keine Dokumente")} /></div>
        ) : (
          <div className="divide-y divide-white/5">
            {docs.map((d) => {
              const Icon = CAT_ICON[d.category] || File;
              return (
                <div key={d.id} className="p-4 flex items-center gap-4" data-testid={`admin-doc-${d.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#A3E635]/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#A3E635]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{d.title}</span>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300">{d.category_label}</span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${d.visibility === "internal" ? "border-amber-400/30 bg-amber-400/10 text-amber-300" : "border-[#A3E635]/30 bg-[#A3E635]/10 text-[#A3E635]"}`} data-testid={`admin-doc-vis-${d.id}`}>
                        {d.visibility === "internal" ? <Lock className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {d.visibility_label}
                      </span>
                    </div>
                    {d.note && <p className="text-sm text-gray-400 mt-0.5 truncate">{d.note}</p>}
                    <div className="text-xs text-gray-500 mt-1">{fmtDate(d.created_at)} · {fmtSize(d.file_size)} · {t("Hochgeladen von")} {d.uploaded_by_name || "KEWAG"}</div>
                  </div>
                  {canWrite && (
                    <button onClick={() => toggleVisibility(d)} data-testid={`admin-doc-vis-toggle-${d.id}`}
                      className="shrink-0 text-xs font-semibold px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition-colors flex items-center gap-1.5"
                      title={d.visibility === "internal" ? t("Für Kunde sichtbar schalten") : t("Auf intern setzen")}>
                      {d.visibility === "internal" ? <><Eye className="w-4 h-4 text-[#A3E635]" /> {t("Freigeben")}</> : <><Lock className="w-4 h-4 text-amber-300" /> {t("Intern")}</>}
                    </button>
                  )}
                  <button onClick={() => download(d)} data-testid={`admin-doc-download-${d.id}`}
                    className="shrink-0 p-2.5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors" title={t("Herunterladen")}>
                    <Download className="w-4 h-4" />
                  </button>
                  {canWrite && (
                    delId === d.id ? (
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => remove(d.id)} data-testid={`admin-doc-confirm-del-${d.id}`}
                          className="text-xs font-bold text-red-400 hover:text-red-300">{t("Dokument wirklich löschen?")}</button>
                        <button onClick={() => setDelId(null)} className="text-xs text-gray-500 hover:text-white">✕</button>
                      </div>
                    ) : (
                      <button onClick={() => setDelId(d.id)} data-testid={`admin-doc-del-${d.id}`}
                        className="shrink-0 p-2.5 rounded-xl border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 transition-colors" title={t("Löschen")}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
